import { useEffect, useMemo, useCallback, useState, useRef } from 'react';

import {
  Badge,
  Drawer,
  List,
  ListItem,
  Tooltip,
  IconButton,
} from '@material-ui/core';
import { makeStyles, alpha } from '@material-ui/core/styles';
import ChatBubbleIcon from '@material-ui/icons/ChatBubble';
import EventAvailableIcon from '@material-ui/icons/EventAvailable';
import ForumIcon from '@material-ui/icons/Forum';
import HistoryIcon from '@material-ui/icons/History';
import PhoneIcon from '@material-ui/icons/Phone';
import clsx from 'clsx';
import { useSnackbar } from 'notistack';
import { Link as RouterLink, useRouteMatch } from 'react-router-dom';

import ButtonChatInterno from 'components/ButtonChatInterno';
import * as socketEvents from 'constants/socketEvents';
import useCurrentUser from 'hooks/useCurrentUser';
import { Sounds, useSound } from 'hooks/useSound';
import socket from 'services/socket';
import { AtendimentoStatusInterface } from 'store/reducers/atendimentos';
import {
  ContarNovaMensagemGrupoInterface,
  ContarNovaMensagemInterface,
  MensagemInterface,
} from 'store/reducers/mensagens';

const drawerWidth = 56;
const navItems = [
  {
    path: '/',
    name: 'Atendimentos',
    icon: ChatBubbleIcon,
  },
  {
    path: '/grupos',
    name: 'Grupos',
    icon: ForumIcon,
  },
  {
    path: '/contatos',
    name: 'Contatos',
    icon: PhoneIcon,
  },
  {
    path: '/historico',
    name: 'Histórico',
    icon: HistoryIcon,
  },
  {
    path: '/agendamentos',
    name: 'Agendamentos',
    icon: EventAvailableIcon,
  },
];

const useStyles = makeStyles(({ palette, spacing }) => ({
  drawer: {
    width: drawerWidth,
    flexShrink: 0,
  },
  drawerPaper: {
    width: drawerWidth,
    position: 'relative',
    alignItems: 'center',
    border: 0,
  },
  listItem: {
    padding: spacing(1, 0),
    color: palette.text.disabled,
  },
  iconButtonSelected: {
    color: palette.primary.main,
    backgroundColor: alpha(palette.primary.main, 0.1),
  },
}));

export default function NavBar() {
  const classes = useStyles();
  const { play } = useSound();
  const { enqueueSnackbar } = useSnackbar();
  const match = useRouteMatch();
  const currentUser = useCurrentUser();
  const selectedPath = useMemo(() => match.path, [match]);
  const [countMsgsChat, setCountMsgsChat] = useState(0);
  const [manuallyUnreadChatIds, setManuallyUnreadChatIds] = useState<
    Set<string>
  >(new Set());
  const [countMsgsGrupos, setCountMsgsGrupos] = useState(
    new Map<string, number>(),
  );
  const lastMessagesRef = useRef<{
    [atendimentoId: string]: MensagemInterface;
  }>({});

  const handlerContarMensagem = useCallback(
    (data: ContarNovaMensagemInterface) => {
      const { atendimentoId, posicao, totalNaoLidas: total } = data;

      // Ignora mensagens que não sejam de atendimentos iniciados com um atendente
      if (posicao !== 'ATENDENTE') return;

      if (total === -1) {
        // Marcado manualmente como não lido — registra o indicador visual (bolinha sem número)
        if (selectedPath === '/') return;
        setManuallyUnreadChatIds(prev => new Set(prev).add(atendimentoId));
        return;
      }

      if (total === 0) {
        // Lido — remove do conjunto de "não lidos manuais"
        setManuallyUnreadChatIds(prev => {
          const next = new Set(prev);
          next.delete(atendimentoId);
          return next;
        });
        return;
      }

      // Nova mensagem real
      if (selectedPath === '/') return;
      const lastMsg = lastMessagesRef.current[atendimentoId];
      if (!lastMsg || lastMsg.autor !== 'AUTO') {
        play(Sounds.NOVA_MENSAGEM);
      }
      setCountMsgsChat(prevState => prevState + 1);
    },
    [play, selectedPath],
  );

  const handlerMensagemGrupo = useCallback(
    (data: ContarNovaMensagemGrupoInterface) => {
      const { grupoChatId, totalNaoLidas } = data;

      // Toca som apenas para novas mensagens reais (não para marcação manual)
      if (totalNaoLidas > 0) play(Sounds.NOVA_MENSAGEM_GRUPO);

      setCountMsgsGrupos(
        prevState => new Map(prevState.set(grupoChatId, totalNaoLidas)),
      );
    },
    [play],
  );

  // Notifica o usuário quando um atendimento é transferido forçadamente para ele
  const handlerTransferenciaForcada = useCallback(
    (data: AtendimentoStatusInterface) => {
      if (data.status !== 'transferencia-forcada') return;

      play(Sounds.FILA_ESPERA);

      const nomeQuemTransferiu = data.usuarioTransferiu?.nome;
      const mensagem = nomeQuemTransferiu
        ? `${nomeQuemTransferiu} transferiu um atendimento para você.`
        : 'Você recebeu uma transferência de atendimento.';

      enqueueSnackbar(mensagem, { variant: 'info' });
    },
    [play, enqueueSnackbar],
  );

  // Monitorar e contar as novas mensagens que chegam nos atendimentos iniciados e nos grupos
  useEffect(() => {
    const handleReceber = (msg: MensagemInterface) => {
      lastMessagesRef.current[msg.atendimentoId] = msg;
    };

    socket.on(socketEvents.ON_RECEBER_MENSAGEM, handleReceber);
    socket.on(socketEvents.ON_CONTAR_NOVA_MENSAGEM, handlerContarMensagem);
    socket.on(socketEvents.ON_CONTAR_NOVA_MENSAGEM_GRUPO, handlerMensagemGrupo);
    return () => {
      socket.off(socketEvents.ON_RECEBER_MENSAGEM, handleReceber);
      socket.off(socketEvents.ON_CONTAR_NOVA_MENSAGEM, handlerContarMensagem);
      socket.off(
        socketEvents.ON_CONTAR_NOVA_MENSAGEM_GRUPO,
        handlerMensagemGrupo,
      );
    };
  }, [handlerContarMensagem, handlerMensagemGrupo]);

  // Listener isolado para não duplicar notificação de transferência forçada
  useEffect(() => {
    socket.on(socketEvents.ON_ATENDIMENTO_STATUS, handlerTransferenciaForcada);

    return () => {
      socket.off(
        socketEvents.ON_ATENDIMENTO_STATUS,
        handlerTransferenciaForcada,
      );
    };
  }, [handlerTransferenciaForcada]);

  // Reseta os contadores de mensagens do Chat ao ir para a pagina do chat
  useEffect(() => {
    if (selectedPath === '/') {
      setCountMsgsChat(0);
      setManuallyUnreadChatIds(new Set());
    }
  }, [selectedPath]);

  const getTotalCountGrupo = () => {
    if (countMsgsGrupos.size === 0) return 0;
    // Soma apenas valores positivos (ignora o -1 de "marcado manualmente")
    return [...countMsgsGrupos.values()]
      .filter(v => v > 0)
      .reduce((acc, v) => acc + v, 0);
  };

  const hasManuallyUnreadGrupo = [...countMsgsGrupos.values()].some(
    v => v === -1,
  );

  const renderNavItems = navItems.map(navItem => {
    const { path, name, icon: NavIcon } = navItem;
    const selected = path === selectedPath;
    const totalMsgsChat = path === '/' ? countMsgsChat : 0;
    const hasManuallyUnreadChat =
      path === '/' ? manuallyUnreadChatIds.size > 0 : false;
    const totalMsgsGrupo = path === '/grupos' ? getTotalCountGrupo() : 0;
    const hasUnreadGrupo = path === '/grupos' ? hasManuallyUnreadGrupo : false;

    // Conteúdo do badge: número se houver mensagens reais, string vazia (bolinha) se apenas marcado manualmente
    const numericBadge = totalMsgsChat || totalMsgsGrupo;
    const badgeContent =
      numericBadge || hasManuallyUnreadChat || hasUnreadGrupo
        ? numericBadge || ''
        : 0;

    // Remove o nav item de Grupos se o usuário não tiver acesso ao recurso de grupos
    if (path === '/grupos' && !currentUser?.modulos.is_access_grupo_modulo) {
      return null;
    }

    return (
      <Tooltip key={path} title={name} placement="right" arrow>
        <ListItem className={classes.listItem} disableGutters>
          <IconButton
            to={path}
            component={RouterLink}
            className={clsx({ [classes.iconButtonSelected]: selected })}
            color="inherit"
            style={{ padding: 6 }}
          >
            <Badge color="error" overlap="circular" badgeContent={badgeContent}>
              <NavIcon />
            </Badge>
          </IconButton>
        </ListItem>
      </Tooltip>
    );
  });

  return (
    <>
      <Drawer
        className={classes.drawer}
        classes={{ paper: classes.drawerPaper }}
        aria-label="Barra de navegação lateral"
        variant="permanent"
      >
        <List>{renderNavItems}</List>
      </Drawer>

      <ButtonChatInterno />
    </>
  );
}
